/**
 * Meta match-key shaping shared by the browser pixel and the server's CAPI.
 *
 * Both sides must derive the SAME values from the same order, or the hashes they
 * produce diverge and Meta stops matching the deduplicated pair — a failure that
 * shows up as good coverage and bad attribution, which is very hard to spot. So
 * the two decisions that aren't pure formatting (what counts as a surname, what
 * country string means) live here, once.
 *
 * Everything else (lowercasing, punctuation, hashing) is applied by fbevents.js
 * in the browser and by server/lib/metaCapi.ts on the server.
 */

/** ISO-3166 alpha-2 (lowercase) for the countries our checkout offers. */
const COUNTRY_ISO2: Record<string, string> = {
  malaysia: "my",
  singapore: "sg",
  brunei: "bn",
  indonesia: "id",
  thailand: "th",
};

/** "Malaysia" → "my". An already-ISO2 value passes through; anything else → "". */
export function countryToIso2(value: string | null | undefined): string {
  const v = (value ?? "").trim().toLowerCase();
  if (COUNTRY_ISO2[v]) return COUNTRY_ISO2[v];
  return /^[a-z]{2}$/.test(v) ? v : "";
}

/**
 * Split a typed name into Meta's fn/ln. A single token yields fn only —
 * inventing a surname would hash to something no real person matches.
 */
export function splitFullName(value: string | null | undefined): { first: string; last: string } {
  const parts = (value ?? "").trim().split(/\s+/).filter(Boolean);
  return { first: parts[0] ?? "", last: parts.slice(1).join(" ") };
}
